import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    Output,
} from '@angular/core';
import { ILink, IMegaMenuSettings, IMenuSpot, INavigationResponse } from '@ncg/data';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { LazyContentAliases } from '../../../$lazy-content/models/lazy-content.models';
import { SettingsService } from '../../../core/settings.service';
import { SpotsConfig } from '../../../spots/spots-config';
import { ModelsAspectRatio } from '../../../utils/helpers/aspect-ratio';

@Component({
    selector: 'ncg-mobile-nav-list',
    template: `
        <ng-content></ng-content>

        <ng-container *ngIf="currentItem">
            <ng-container
                *ngIf="
                    currentItem.pageLink && (currentItem.template === 'linkPage' || currentItem.template === 'modelLinkPage');
                    else notLinkPageTmpl
                "
            >
                <ng-container *ngIf="currentItem.pageLink?.isExternal; else internalLink">
                    <a class="mobile-menu__goto" rel="noopener" [href]="currentItem.pageLink?.url" [attr.target]="currentItem.pageLink?.target">
                        {{ currentItem.name }}
                    </a>
                </ng-container>
                <ng-template #internalLink>
                    <a class="mobile-menu__goto" [routerLink]="currentItem.pageLink?.url || currentItem.url">{{ currentItem.name }}</a>
                </ng-template>
            </ng-container>
            <ng-template #notLinkPageTmpl>
                <a class="mobile-menu__goto" [routerLink]="currentItem.url">
                    <ng-container *ngIf="currentItem.nameOnMobile; else nameTmlp">{{ currentItem.nameOnMobile }} </ng-container>
                    <ng-template #nameTmlp>
                        <span *ngIf="spotsConfig.isMobileBackMultiLevel">{{ 'navigation.go_to' | translate }} </span>
                        <span>{{ currentItem.name }}</span>
                    </ng-template>
                </a>
            </ng-template>
        </ng-container>

        <ng-container *ngFor="let item of menuList; trackBy: trackByMethod">
            <ng-container *ngIf="item?.includeInNavigation && !item.hideInMenu">
                <ng-container
                    *ngIf="
                        item.template === 'modelPage' || item.template === 'campaignPage' || item.template === 'modelLinkPage';
                        then isModelPageTmpl;
                        else isNotModelPageTmpl
                    "
                ></ng-container>

                <ng-template #isModelPageTmpl>
                    <ng-container *ngIf="item.pageLink?.isExternal; else internalLink">
                        <a
                            class="mobile-menu__item mobile-menu__model is-flex"
                            [href]="item.pageLink?.url"
                            [attr.target]="item.pageLink?.target"
                            routerLinkActive="active"
                        >
                            <ng-container *ngTemplateOutlet="modelPageContentTmpl"></ng-container>
                        </a>
                    </ng-container>
                    <ng-template #internalLink>
                        <a
                            class="mobile-menu__item mobile-menu__model is-flex"
                            [routerLink]="item.pageLink?.url || item.url"
                            routerLinkActive="active"
                        >
                            <ng-container *ngTemplateOutlet="modelPageContentTmpl"></ng-container>
                        </a>
                    </ng-template>
                    <ng-template #modelPageContentTmpl>
                        <picture *ngIf="item.image as image" class="mobile-model__image is-ratio-model">
                            <source [srcset]="image | imageUrl: { width: 116, heightratio: modelsAspectRatio, mode: 'crop' } : 2" />
                            <img
                                [src]="image | imageUrl: { width: 116, heightratio: modelsAspectRatio, mode: 'crop' }"
                                [alt]="item.image.altText || item.name"
                                loading="lazy"
                                ncgImageLoad
                            />
                        </picture>
                        <div class="mobile-model__header is-flex is-justify-content-center is-flex-direction-column">
                            <p class="mobile-model__title">{{ item.name }}</p>
                            <p class="mobile-model__text" *ngIf="item.priceFormatted">
                                {{ 'models.pricefrom' | translate: { price: item.priceFormatted } }}
                            </p>
                        </div>
                    </ng-template>
                </ng-template>

                <ng-template #isNotModelPageTmpl>
                    <!-- Check if item has children or not -->
                    <ng-container
                        *ngIf="item.children?.length && !item.hideChildrenOnMobile; then itemHasChildrenTmpl; else itemHasNoChildrenTmpl"
                    ></ng-container>

                    <!-- item has children -->
                    <ng-template #itemHasChildrenTmpl>
                        <button class="mobile-menu__item level is-flex" (click)="onNavigateClicked(item)">
                            <span>{{ item.name }}</span>
                            <svg-icon-sprite
                                [src]="spotsConfig.iconType === 'light' ? 'chevron-light-right' : 'chevron-right'"
                                [viewBox]="'0 0 30 30'"
                                [width]="'30px'"
                                [height]="'30px'"
                                aria-hidden="true"
                                class="is-flex mobile-menu__icon"
                            ></svg-icon-sprite>
                        </button>
                    </ng-template>

                    <!-- item has no children -->
                    <ng-template #itemHasNoChildrenTmpl>
                        <ng-container *ngIf="item.pageLink?.isExternal; else internalLink">
                            <a
                                class="mobile-menu__item level is-flex"
                                rel="noopener"
                                [href]="item.pageLink?.url"
                                [attr.target]="item.pageLink?.target"
                            >
                                {{ item.name }}
                            </a>
                        </ng-container>
                        <ng-template #internalLink>
                            <a class="mobile-menu__item level is-flex" [routerLink]="[item.pageLink?.url || item.url]" routerLinkActive="active">{{
                                item.name
                            }}</a>
                        </ng-template>
                    </ng-template>
                </ng-template>
            </ng-container>
        </ng-container>

        <!--Menu spot (model overview page only)-->
        <ng-template
            *ngIf="currentItem?.template === 'modelOverviewPage' && menuSpot?.enableMenuSpot"
            ncgLazyComponentLoader
            [data]="menuSpot"
            [alias]="menuSpotKey"
        ></ng-template>

        <!--Mega menu items (alternate menu only)-->
        <ng-template
            *ngIf="altMenuSettings.iconLinks?.length"
            ncgLazyComponentLoader
            [data]="altMenuSettings"
            [alias]="altMenuIconLinksKey"
        ></ng-template>
        <ng-template
            *ngIf="altMenuSettings.highlightImage?.url"
            ncgLazyComponentLoader
            [data]="altMenuSettings"
            [alias]="altMenuHighlightKey"
        ></ng-template>

        <!--Mega menu shortcuts (non-alternate menu only)-->
        <ncg-shortcuts *ngIf="isContentLoaded && !isTopLevelList && !altMenuSettings.iconLinks?.length" [isInMobile]="true"></ncg-shortcuts>

        <!--Meta menu (top level only)-->
        <ncg-meta-menu *ngIf="isContentLoaded && isTopLevelList" [isInMobile]="true"></ncg-meta-menu>

        <!--Top menu (top level only)-->
        <ncg-top-menu *ngIf="isContentLoaded && isTopLevelList && topMenu?.length" [links]="topMenu"></ncg-top-menu>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileNavListComponent implements OnDestroy, OnChanges, AfterViewInit {
    private readonly unsubscribe = new Subject<void>();

    public isContentLoaded?: boolean;
    public modelsAspectRatio = ModelsAspectRatio;
    public menuSpotKey = LazyContentAliases.MenuSpot;
    public altMenuHighlightKey = LazyContentAliases.AltMenuHighlight;
    public altMenuIconLinksKey = LazyContentAliases.AltMenuIconLinks;
    public altMenuSettings: IMegaMenuSettings = {};
    public menuSpot?: IMenuSpot;
    public topMenu?: ILink[];

    @Input()
    public isTopLevelList?: boolean;

    @Input()
    public menuList?: INavigationResponse[];

    @Input()
    public currentItem: INavigationResponse;

    @Input()
    public currentPath?: string;

    @Output()
    public navigateTo: EventEmitter<INavigationResponse> = new EventEmitter();

    constructor(
        private readonly cd: ChangeDetectorRef,
        public readonly settings: SettingsService,
        @Inject(SpotsConfig)
        public readonly spotsConfig: SpotsConfig
    ) {
        this.settings
            .get()
            .pipe(take(1), takeUntil(this.unsubscribe))
            .subscribe((s) => {
                this.menuSpot = s.menuSpot;
                this.topMenu = s.hasTopMenu ? s.topMenuLinks : undefined;
                this.cd.markForCheck();
            });
    }

    public ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public ngOnChanges(): void {
        this.altMenuSettings = this.currentItem?.megaMenu ?? {};
        this.cd.markForCheck();
    }

    public ngAfterViewInit() {
        this.isContentLoaded = true;
    }

    public onNavigateClicked(item: INavigationResponse) {
        this.navigateTo.emit(item);
    }

    public trackByMethod(index: number, item: any) {
        return item.id || index;
    }
}
